/**
 * Главный компонент приложения
 */
import { useMemo, useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { QRPage } from './pages/QRPage'
import { OnboardingPage } from './pages/OnboardingPage'
import { InstallPage } from './pages/InstallPage'
import { ProfilePage } from './pages/ProfilePage'
import { SitesPage } from './pages/SitesPage'
import { SchedulePage } from './pages/SchedulePage'
import { EmployeeDeletedScreen } from './components/EmployeeDeletedScreen'
import { useWorkerStore } from './store/workerStore'
import { ScreenshotProtection } from './services/screenshotProtection'
import { UpdateNotification } from './components/UpdateNotification'
import { ScrollToTop } from './components/ScrollToTop'
import { PushPromptModal } from './components/PushPromptModal'
import { apiClient } from './services/apiClient'
import './App.css'

// Функция для проверки, установлено ли приложение как PWA
function isPWAInstalled(): boolean {
  // Проверка для Android и современных браузеров
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true
  }
  
  // Проверка для fullscreen режима
  if (window.matchMedia('(display-mode: fullscreen)').matches) {
    return true
  }
  
  // Проверка для iOS Safari
  if ((window.navigator as any).standalone === true) {
    return true
  }
  
  // Проверка через referrer для Android (только если referrer не пустой)
  if (document.referrer && document.referrer.includes('android-app://')) {
    return true
  }
  
  return false
}

// Компонент с логикой приложения (внутри BrowserRouter)
function AppContent() {
  const navigate = useNavigate()
  const [employeeCheckDone, setEmployeeCheckDone] = useState(false)

  // Логируем первый запуск для отладки
  useEffect(() => {
    console.log('[App] Initial load:', {
      url: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      isPWA: isPWAInstalled(),
      referrer: document.referrer
    })
  }, [])
  
  // Используем хук для реактивности
  let isOnboarded = false
  try {
    isOnboarded = useWorkerStore((state) => state.isOnboarded)
  } catch (error) {
    console.error('Failed to read store, clearing localStorage:', error)
    // Очищаем поврежденные данные
    try {
      localStorage.removeItem('worker-storage')
      // Не перезагружаем сразу, даем пользователю увидеть ошибку
    } catch (clearError) {
      console.error('Failed to clear localStorage:', clearError)
    }
  }
  
  // Мемоизируем проверку установки PWA
  const isInstalled = useMemo(() => {
    const installed = isPWAInstalled()
    // Логирование для отладки (можно убрать в production)
    console.log('[PWA Check]', {
      installed,
      displayMode: window.matchMedia('(display-mode: standalone)').matches ? 'standalone' : 
                   window.matchMedia('(display-mode: fullscreen)').matches ? 'fullscreen' : 'browser',
      iosStandalone: (window.navigator as any).standalone,
      referrer: document.referrer ? document.referrer.substring(0, 50) : 'empty'
    })
    return installed
  }, [])
  
  // Проверяем параметр URL для принудительного показа страницы установки
  const showInstall = useMemo(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search)
      return params.get('install') === 'true'
    }
    return false
  }, [])
  
  // Проверяем наличие onboarding параметров в URL (включая start_url из dynamic manifest)
  const hasOnboardingParams = useMemo(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search)
      return params.has('employee_id') && params.has('token')
    }
    return false
  }, [])
  
  // Получаем employeeId для отправки нарушений
  const employeeId = useWorkerStore((state) => state.employeeId)
  
  // Автоматическая проверка существования сотрудника при запуске
  useEffect(() => {
    const checkEmployeeExists = async () => {
      // Проверяем только если:
      // 1. Сотрудник прошел онбординг
      // 2. Есть интернет
      // 3. Проверка еще не выполнялась
      if (!isOnboarded || !navigator.onLine || employeeCheckDone || !employeeId) {
        return
      }

      console.log('[App] Checking if employee exists...')
      
      // Задержка для стабильности (даем приложению инициализироваться)
      await new Promise(resolve => setTimeout(resolve, 2000))

      try {
        // Выполняем запрос к API
        await apiClient.get(`/api/v1/worker/employee/${employeeId}`)
        console.log('[App] Employee exists, all good')
        setEmployeeCheckDone(true)
      } catch (error) {
        console.error('[App] Failed to check employee:', error)
        
        // Проверяем, является ли это ошибкой удаленного сотрудника
        const { EmployeeDeletedError } = await import('./errors/EmployeeDeletedError')
        if (error instanceof EmployeeDeletedError) {
          console.warn('[App] Employee was deleted, redirecting...')
          navigate('/employee-deleted')
        }
        
        setEmployeeCheckDone(true)
      }
    }

    checkEmployeeExists()
  }, [isOnboarded, employeeId, employeeCheckDone, navigate])

  // Включаем защиту от скриншотов
  useEffect(() => {
    ScreenshotProtection.enable()
    
    // Подписываемся на попытки скриншота
    const unsubscribe = ScreenshotProtection.onScreenshotAttempt(() => {
      // Показываем предупреждение
      ScreenshotProtection.showWarning()
      
      // Отправляем уведомление на сервер
      if (employeeId) {
        ScreenshotProtection.reportScreenshotAttempt(employeeId)
      }
    })
    
    return () => {
      unsubscribe()
      ScreenshotProtection.disable()
    }
  }, [employeeId])
  
  return (
    <>
      <ScrollToTop />
      <UpdateNotification />
      <PushPromptModal />
      <Routes>
        <Route
          path="/install"
          element={<InstallPage />}
        />
        <Route
          path="/onboarding"
          element={
            !isInstalled && hasOnboardingParams ? (
              <Navigate to={`/install${window.location.search}`} replace />
            ) : (
              <OnboardingPage />
            )
          }
        />
        <Route
          path="/profile"
          element={
            showInstall || !isInstalled ? (
              <Navigate to={`/install${hasOnboardingParams ? window.location.search : ''}`} replace />
            ) : isOnboarded ? (
              <ProfilePage />
            ) : (
              <Navigate to={`/onboarding${hasOnboardingParams ? window.location.search : ''}`} replace />
            )
          }
        />
        <Route
          path="/sites"
          element={
            showInstall || !isInstalled ? (
              <Navigate to={`/install${hasOnboardingParams ? window.location.search : ''}`} replace />
            ) : isOnboarded ? (
              <SitesPage />
            ) : (
              <Navigate to={`/onboarding${hasOnboardingParams ? window.location.search : ''}`} replace />
            )
          }
        />
        <Route
          path="/schedule"
          element={
            showInstall || !isInstalled ? (
              <Navigate to={`/install${hasOnboardingParams ? window.location.search : ''}`} replace />
            ) : isOnboarded ? (
              <SchedulePage />
            ) : (
              <Navigate to={`/onboarding${hasOnboardingParams ? window.location.search : ''}`} replace />
            )
          }
        />
        <Route
          path="/"
          element={
            showInstall || !isInstalled ? (
              <Navigate to={`/install${hasOnboardingParams ? window.location.search : ''}`} replace />
            ) : isOnboarded ? (
              <QRPage />
            ) : (
              <Navigate to={`/onboarding${hasOnboardingParams ? window.location.search : ''}`} replace />
            )
          }
        />
        <Route
          path="/employee-deleted"
          element={<EmployeeDeletedScreen />}
        />
        <Route
          path="*"
          element={<Navigate to="/" replace />}
        />
      </Routes>
    </>
  )
}

// Главный компонент App оборачивает AppContent в BrowserRouter
function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  )
}

export default App

